import { useTranslation } from "react-i18next";
import { TimelineItem } from './TimelineItem';
import {
  events,
  returnEvents,
  returnEventsRu,
  returnProblematicEvents,
  returnProblematicEventsRu,
  reexportEvents,
  reexportEventsNotUNI,
  getExtendedType,
  getErrorIds,
  normalizeTrackEventType,
  EventInfo,
  TrackEventCore,
  ProblemReasonType,
} from "@utils/trackEvents";
import {
  EventStatus,
  ParcelModalTimelineProps,
  ExtendedTrackEvent,
} from "./types"
import "./ParcelModalTimeline.css";

const trackings: Record<string, EventInfo[]> = {
  return: returnEvents,
  returnToRussia: returnEventsRu,
  returnProblematic: returnProblematicEvents,
  returnProblematicToRussia: returnProblematicEventsRu,
  direct: events,
  clientReturn: reexportEvents,
  clientReturnNotUNI: reexportEventsNotUNI,
}

const getTrackingName = (tracking: string) => {
  if (tracking === "clientReturn" || tracking === "clientReturnNotUNI") return "reexportTrackEventTypes";
  if (tracking === "returnToRussia") return "returnTrackEventsRu"
  if (tracking === "returnProblematicToRussia") return "returnTrackEventsRu"
  return "trackEventTypes";
}

const getLastNoErrorEventId = (items: TrackEventCore[], tracking: EventInfo[]) => {
  return [...tracking]
    .reverse()
    .find(v => (
      items.find(item => (
        (item.type === +v.id)
        || (v.childIds || []).includes(item.type))
      )
    ))?.id;
}

const getEventStatus = (
  eventInfo: EventInfo,
  event?: TrackEventCore,
  lastNoErrorEventId?: string,
) => {
  if (event) return getErrorIds(eventInfo, lastNoErrorEventId)
    .includes(getExtendedType(event)) ? EventStatus.ERROR : EventStatus.DONE;
  if (eventInfo.id === "0") return EventStatus.DONE;
  return EventStatus.WAIT;
}

const isMainEvent = (id: number, events: EventInfo[]) => Boolean(
  events.find(item => (item.ids ? item.ids.includes(id) : (String(id) === item.id)))
)

const getEventforIds = (ids: number[], items: TrackEventCore[]) => {
  if (!ids?.length) return undefined
  for (let i = 0; i < ids.length; i++) {
    const res = items.find(v => getExtendedType(v) === ids[i]);
    if (res) return res
  }
}

const getEventforErrorsIds = (
  items: TrackEventCore[],
  eventInfo: EventInfo,
  lastNoErrorEventId?: string,
) => {
  const ids = getErrorIds(eventInfo, lastNoErrorEventId)
  if (!ids?.length) return undefined
  for (let i = 0; i < ids.length; i++) {
    const res = items.find(v => getExtendedType(v) === ids[i]);
    if (res) return res
  }
}

const ParcelModalTimeline = ({ items: rawItems, countryCode, tracking, estimatedLastMileArrivalTime }: ParcelModalTimelineProps) => {
  const { t } = useTranslation();
  const items = rawItems.map(v => ({ ...v, type: normalizeTrackEventType(v.type) }));
  const trackingName = getTrackingName(tracking);
  const lastNoErrorEventId = getLastNoErrorEventId(items, trackings[tracking] ?? []);

  const data: ExtendedTrackEvent[] = (trackings[tracking] ?? [])
    .map((item: EventInfo, eventIdx: number, allItems: EventInfo[]) => {
      const event = getEventforIds(item.ids || [+item.id], items)
        || getEventforErrorsIds(items, item, lastNoErrorEventId);

      return {
        ...event,
        id: event?.id ? String(event.type) : `empty-${item.id}-${eventIdx}`,
        name: t(`${trackingName}.${String(event?.type || item.id)}`, { context: ProblemReasonType[event?.cancellationReasonType ?? ""] || countryCode }),
        status: getEventStatus(item, event, lastNoErrorEventId),
        statuses: item.childIds?.length
          ? items.filter(v => (
            (v.type >= (item?.childIds?.[0] ?? 0))
            && (v.type <= (item?.childIds?.[1] ?? 99999))
            && !isMainEvent(v.type, allItems)
          ))
          : [],
      };
    });

  let lastDoneIdx = -1;
  data.forEach((item: ExtendedTrackEvent, idx: number) => {
    if (item.status === EventStatus.DONE || item.status === EventStatus.ERROR) lastDoneIdx = idx;
  });
  if (lastDoneIdx > -1) {
    for (let idx = 0; idx < lastDoneIdx; idx++) {
      data[idx].status = EventStatus.DONE;
    }
    if (data[lastDoneIdx].status !== EventStatus.ERROR) {
      data[lastDoneIdx].status = EventStatus.ACTIVE;
    }
  }

  return (
    <div className="parcel-modal-pickup-items">
      {data.map((v) => (
        <TimelineItem
          key={v.id}
          trackingName={trackingName}
          countryCode={countryCode}
          estimatedLastMileArrivalTime={estimatedLastMileArrivalTime}
          {...v}
        />
      ))}
    </div>
  )
}

export default ParcelModalTimeline;
